import shaka from 'shaka-player';

const settingsIconUrl = new URL('../assets/icons/settings.svg', import.meta.url).toString();
const rewindIconUrl = new URL('../assets/icons/rewind.svg', import.meta.url).toString();
const forwardIconUrl = new URL('../assets/icons/forward.svg', import.meta.url).toString();
const subtitlesIconUrl = new URL('../assets/icons/subtitles.svg', import.meta.url).toString();
const playbackIconUrl = new URL('../assets/icons/playback.svg', import.meta.url).toString();
const soundIconUrl = new URL('../assets/icons/sound.svg', import.meta.url).toString();
const volumeCutIconUrl = new URL('../assets/icons/volume cut.svg', import.meta.url).toString();
const pipIconUrl = new URL('../assets/icons/pip.svg', import.meta.url).toString();
const fullscreenIconUrl = new URL('../assets/icons/full screen.svg', import.meta.url).toString();
const fullscreenExitIconUrl = new URL('../assets/icons/fullscreen  exit.svg', import.meta.url).toString();

const SHAKA_SVG_ICON_VIEWBOX = '0 -960 960 960';

export type RegisteredShakaIcon = {
  name: string;
  path?: string;
  viewBox?: string;
  url?: string;
};

type IconRegistryShape = {
  register?: ((name: string, icon: { path?: string; viewBox?: string; url?: string } | string) => void) &
    ((icons: Record<string, string | { path?: string; viewBox?: string; url?: string }>) => void);
};

const CUSTOM_ICON_CLASS = 'shaka-custom-icon';
const registeredIcons = new Map<string, RegisteredShakaIcon>();
let defaultsRegistered = false;

function getIconRegistry(): IconRegistryShape | null {
  const ui = shaka.ui as unknown as { IconRegistry?: IconRegistryShape };
  return ui.IconRegistry ?? null;
}

function registerViaShakaIconRegistry(icon: RegisteredShakaIcon): void {
  const registry = getIconRegistry();
  if (!registry?.register) return;
  const payload = icon.url ? icon.url : { path: icon.path, viewBox: icon.viewBox };

  try {
    registry.register(icon.name, payload);
    return;
  } catch {
    // Fall through and try object-map registration shape.
  }

  try {
    registry.register({ [icon.name]: payload });
  } catch {
    // Ignore — runtime fallback will still render icon via helper below.
  }
}

function buildIconMarkup(icon: RegisteredShakaIcon): string {
  if (icon.url) {
    return `<img src="${icon.url}" alt="" width="20" height="20" decoding="async" />`;
  }
  if (icon.path) {
    const viewBox = icon.viewBox || SHAKA_SVG_ICON_VIEWBOX;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" fill="currentColor" aria-hidden="true" focusable="false"><path d="${icon.path}" /></svg>`;
  }
  return '';
}

/**
 * Reusable helper for registering additional icons.
 * Example:
 * `registerShakaIcon({ name: 'bookmark', viewBox: '0 0 24 24', path: 'M6 2h12v20l-6-4-6 4z' });`
 */
export function registerShakaIcon(icon: RegisteredShakaIcon): void {
  if (!icon.path && !icon.url) return;
  const normalized: RegisteredShakaIcon = {
    ...icon,
    viewBox: icon.viewBox || SHAKA_SVG_ICON_VIEWBOX,
  };
  registeredIcons.set(icon.name, normalized);
  registerViaShakaIconRegistry(normalized);
}

/** Registers all built-in custom control icons once per runtime. */
export function ensureDefaultShakaIconsRegistered(): void {
  if (defaultsRegistered) return;
  defaultsRegistered = true;

  registerShakaIcon({ name: 'settings', url: settingsIconUrl });
  registerShakaIcon({ name: 'rewind10', url: rewindIconUrl });
  registerShakaIcon({ name: 'forward10', url: forwardIconUrl });
  registerShakaIcon({ name: 'captions', url: subtitlesIconUrl });
  registerShakaIcon({ name: 'playback_rate', url: playbackIconUrl });
  registerShakaIcon({ name: 'mute', url: soundIconUrl });
  registerShakaIcon({ name: 'volume_cut', url: volumeCutIconUrl });
  registerShakaIcon({ name: 'picture_in_picture', url: pipIconUrl });
  registerShakaIcon({ name: 'fullscreen', url: fullscreenIconUrl });
  registerShakaIcon({ name: 'fullscreen_exit', url: fullscreenExitIconUrl });
}

/** Runtime fallback icon element for controls (used when IconRegistry API is unavailable). */
export function createRegisteredShakaIconElement(iconName: string): HTMLSpanElement {
  const slot = document.createElement('span');
  slot.className = CUSTOM_ICON_CLASS;
  slot.setAttribute('aria-hidden', 'true');
  const icon = registeredIcons.get(iconName);
  if (icon) slot.innerHTML = buildIconMarkup(icon);
  return slot;
}

/**
 * Example helper to replace any existing Shaka control icon in-place:
 * `replaceShakaControlButtonIcon(container, '.shaka-resolution-button', 'settings')`.
 */
const NATIVE_ICON_SELECTOR =
  'i.material-icons-round, .shaka-material-svg-icon, .material-svg-icon, svg, img, .shaka-overflow-playback-rate-mark';

/** Remove Shaka default icon layers; skips icons nested inside our custom icon slot. */
export function stripNativeIconsFromButton(button: HTMLElement): number {
  let removed = 0;
  button.querySelectorAll(NATIVE_ICON_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.closest(`.${CUSTOM_ICON_CLASS}`)) return;
    el.remove();
    removed += 1;
  });
  return removed;
}

function countRemainingNativeIcons(button: HTMLElement): number {
  let count = 0;
  button.querySelectorAll(NATIVE_ICON_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.closest(`.${CUSTOM_ICON_CLASS}`)) return;
    count += 1;
  });
  return count;
}

/** Strip Material icon ligature text Shaka writes via `button.textContent` on stateful controls. */
function stripShakaMaterialIconLayer(button: HTMLElement): void {
  if (button.classList.contains('material-icons-round')) {
    button.classList.remove('material-icons-round');
  }
  for (const node of Array.from(button.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      node.remove();
    }
  }
}

export function replaceShakaControlButtonIcon(
  container: HTMLElement,
  selector: string,
  iconName: string,
): void {
  const button = container.querySelector(selector);
  if (!(button instanceof HTMLElement)) return;

  // Mute/fullscreen set `textContent` on tap (wipes children); always clear ligature text first.
  stripShakaMaterialIconLayer(button);

  stripNativeIconsFromButton(button);

  const existingCustomIcons = button.querySelectorAll(`.${CUSTOM_ICON_CLASS}`);
  const hasMatchingSingleCustomIcon =
    existingCustomIcons.length === 1 &&
    existingCustomIcons[0] instanceof HTMLElement &&
    existingCustomIcons[0].dataset.shakaIconName === iconName;

  if (!hasMatchingSingleCustomIcon) {
    existingCustomIcons.forEach((el) => el.remove());
    const customIconEl = createRegisteredShakaIconElement(iconName);
    customIconEl.dataset.shakaIconName = iconName;
    button.prepend(customIconEl);
  }
}

const guardedButtons = new WeakSet<HTMLElement>();

/** Re-strip native icons when Shaka re-inserts them after `uiupdated` handlers complete. */
export function guardShakaControlButtonIcon(
  container: HTMLElement,
  selector: string,
  iconName: string,
): void {
  const button = container.querySelector(selector);
  if (!(button instanceof HTMLElement) || guardedButtons.has(button)) return;

  guardedButtons.add(button);
  const observer = new MutationObserver(() => {
    const nativeBefore = countRemainingNativeIcons(button);
    if (nativeBefore === 0) return;

    stripNativeIconsFromButton(button);
    const hasCustom =
      button.querySelectorAll(`.${CUSTOM_ICON_CLASS}`).length === 1 &&
      button.querySelector(`.${CUSTOM_ICON_CLASS}`) instanceof HTMLElement &&
      (button.querySelector(`.${CUSTOM_ICON_CLASS}`) as HTMLElement).dataset.shakaIconName === iconName;
    if (!hasCustom) {
      replaceShakaControlButtonIcon(container, selector, iconName);
    }
  });

  observer.observe(button, { childList: true, subtree: true });
}
